<template>
    <v-container fluid class="pa-6">
        <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
            <div>
                <h2 class="text-h6">Clienti</h2>
                <div class="text-body-2 text-medium-emphasis">Classifica per valore dei preventivi (IVA esclusa)</div>
            </div>
            <filtro-periodo />
        </div>

        <v-row v-if="dati">
            <v-col cols="12" sm="6" md="4">
                <v-card variant="outlined">
                    <v-card-text>
                        <div class="text-body-2 text-medium-emphasis">Clienti con preventivi</div>
                        <div class="text-h5 font-weight-bold my-1">{{ formatNumero(dati.clientiTotali) }}</div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="4">
                <v-card variant="outlined">
                    <v-card-text>
                        <div class="text-body-2 text-medium-emphasis">Quota dei primi 10 clienti</div>
                        <div class="text-h5 font-weight-bold my-1">{{ formatPerc(dati.quotaTop10) }}</div>
                        <div class="text-caption text-medium-emphasis">sul valore totale del periodo</div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-card variant="outlined" class="mt-4" :loading="caricamento">
            <v-card-title class="d-flex align-center">
                Migliori clienti
                <v-spacer />
                <v-select v-model="top" :items="[10, 20, 50]" label="Mostra" density="compact" hide-details
                    style="max-width: 120px" @update:model-value="carica" />
            </v-card-title>
            <v-card-text v-if="dati && dati.top.length">
                <v-table density="compact">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Cod. cliente</th>
                            <th class="text-right">Preventivi</th>
                            <th class="text-right">Valore</th>
                            <th style="min-width: 180px">Quota sul totale</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(c, i) in dati.top" :key="c.codCliente">
                            <td>{{ i + 1 }}</td>
                            <td>{{ c.codCliente }}</td>
                            <td class="text-right">{{ formatNumero(c.n) }}</td>
                            <td class="text-right">{{ formatEuro(c.valore) }}</td>
                            <td>
                                <div class="d-flex align-center">
                                    <v-progress-linear :model-value="c.quota / quotaMassima * 100" height="8" rounded
                                        color="primary" style="max-width: 110px" />
                                    <span class="text-caption ml-2">{{ formatPerc(c.quota) }}</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-table>
            </v-card-text>
            <v-card-text v-else-if="!caricamento" class="text-medium-emphasis">Nessun preventivo nel periodo.</v-card-text>
        </v-card>

        <v-card variant="outlined" class="mt-4" :loading="caricamento">
            <v-card-title>Clienti attivi per anno</v-card-title>
            <v-card-subtitle>Nuovo = primo preventivo di sempre in quell'anno; ricorrente = già cliente in precedenza</v-card-subtitle>
            <v-card-text>
                <div v-if="dati && dati.perAnno.length" style="height: 320px">
                    <Bar :data="datiGrafico" :options="opzioniGrafico" />
                </div>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { Bar } from "vue-chartjs";
import { COLORI } from "@/utils/chart.js";
import Functions from "@/utils/functions.js";
import periodo from "@/mixins/periodo.js";
import FiltroPeriodo from "@/components/statistiche/FiltroPeriodo.vue";

export default {
    name: "ClientiPage",
    components: { FiltroPeriodo, Bar },
    mixins: [periodo],

    data() {
        return {
            dati: null,
            top: 10,
            caricamento: false,
        };
    },

    computed: {
        // Le barre di quota sono relative al primo cliente, per leggerle meglio.
        quotaMassima() {
            return this.dati && this.dati.top.length ? Math.max(this.dati.top[0].quota, 0.01) : 1;
        },

        datiGrafico() {
            const anni = this.dati.perAnno;
            return {
                labels: anni.map((a) => String(a.anno)),
                datasets: [
                    { label: "Nuovi", data: anni.map((a) => a.nuovi), backgroundColor: COLORI.primary },
                    { label: "Ricorrenti", data: anni.map((a) => a.ricorrenti), backgroundColor: COLORI.grigio },
                ],
            };
        },

        opzioniGrafico() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: { stacked: true, grid: { display: false } },
                    y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
                },
                plugins: {
                    legend: { position: "bottom" },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => ctx.dataset.label + ": " + Functions.formatNumero(ctx.parsed.y),
                            footer: (items) => "Attivi: " + Functions.formatNumero(items.reduce((s, i) => s + i.parsed.y, 0)),
                        },
                    },
                },
            };
        },
    },

    methods: {
        formatEuro: Functions.formatEuro,
        formatNumero: Functions.formatNumero,
        formatPerc: Functions.formatPerc,

        async carica() {
            this.caricamento = true;
            const periodoRichiesto = this.periodo;
            const topRichiesto = this.top;
            try {
                const dati = await this.$store.dispatch("statistiche/clienti", topRichiesto);
                if (periodoRichiesto === this.periodo && topRichiesto === this.top) this.dati = dati;
            } catch (error) {
                console.log(error);
            }
            this.caricamento = false;
        },
    },
};
</script>
